import type { FragmentType } from '@/generated/gql/fragment-masking'
import { useFragment } from '@/generated/gql/fragment-masking'
import { graphql } from '@/generated/gql/gql'
import { numberFormat } from '@/helpers/numbers'
import AccessTime from '@mui/icons-material/AccessTime'
import Place from '@mui/icons-material/Place'
import {
  Button,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Stack,
  Typography,
} from '@mui/material'

import React from 'react'

import { formatTimeslotTime } from '../timeslotDates'
import ItemCard from './shared/ItemCard'

const canteenOrderedItemCardOrderItemFragment = graphql(`
  fragment CanteenOrderedItemCard_OrderItemFragment on CCOrderItem {
    id
    name
    qty
    unitPrice
    ccSupplierOrder {
      arrivalTime
    }
    vendor {
      companyName
      logo
    }
    accountLocation {
      timezone
      id
      label
    }
    canCancel
    liveMenuItem {
      coreLiveItem {
        image
      }
    }
  }
`)

interface CCOrderedItemCardProps {
  orderItem: FragmentType<typeof canteenOrderedItemCardOrderItemFragment>
  pendingCancellation?: boolean
  hidePrices: boolean
  cancelOrder: (ccOrderItemId: number) => void
  showItemDetails: (itemId: string) => void
}

function CCOrderedItemCardView({
  orderItem: orderItemProps,
  hidePrices,
  pendingCancellation,
  cancelOrder,
  showItemDetails,
}: CCOrderedItemCardProps) {
  const orderItem = useFragment(canteenOrderedItemCardOrderItemFragment, orderItemProps)

  return (
    <ItemCard
      pendingRemoval={pendingCancellation}
      vendorAvatar={orderItem.vendor.logo}
      vendorName={orderItem.vendor.companyName}
      imageTitle={orderItem.name}
      imageUrl={orderItem.liveMenuItem.coreLiveItem?.image || ''}>
      <CardHeader title={orderItem.name} />
      <CardContent>
        <Stack marginBottom={1} spacing={1} direction="column" flexWrap="wrap">
          <Chip
            sx={{ width: 'fit-content' }}
            icon={<AccessTime />}
            size="small"
            label={formatTimeslotTime(
              orderItem.ccSupplierOrder?.arrivalTime,
              orderItem.accountLocation.timezone,
            )}
          />
          <Chip
            sx={{ width: 'fit-content' }}
            size="small"
            icon={<Place />}
            label={orderItem.accountLocation.label}
          />
        </Stack>
        {!hidePrices ? (
          <Typography textAlign="right">
            {orderItem.qty} x {numberFormat(orderItem.unitPrice)}
          </Typography>
        ) : null}
      </CardContent>
      <CardActions>
        <Stack width="100%" direction="row-reverse" alignItems="center" spacing={0.5}>
          <Button
            onClick={() => {
              cancelOrder(orderItem.id)
            }}
            disabled={!orderItem.canCancel}
            size="small"
            variant="contained"
            color="error">
            Cancel
          </Button>
          <Button
            onClick={() => {
              showItemDetails(orderItem.id.toString())
            }}
            size="small"
            variant="contained"
            color="primary">
            See more
          </Button>
        </Stack>
      </CardActions>
    </ItemCard>
  )
}

export const CCOrderedItemCard = React.memo(CCOrderedItemCardView, (prev, next) => {
  return (
    prev.hidePrices === next.hidePrices &&
    prev.pendingCancellation === next.pendingCancellation &&
    prev.orderItem === next.orderItem
  )
})
